import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link, navigate } from '../../app/router';
import { Button, FilePicker, linkClass } from '../../ui';
import { useInvalidateContent } from './queries';

type Report = { boardId: string; objects: number; skipped: number; missing: number; failed: number };
type State = { kind: 'idle' } | { kind: 'running'; step: 'read' | 'assets' | 'seed'; done: number; total: number } | { kind: 'error'; code: string } | { kind: 'report'; report: Report };

/**
 * "Import a board" on a project page (spec §12): a `.osboard` or `BoardFile` JSON becomes a new board of the project.
 * Opens it directly, or first says what could not be imported. The import modules load on first use.
 */
/**
 * Board import (P9) as a hook (P16-4): `pick` opens the file chooser (from a button or a menu item), `ui` is the hidden
 * input with the progress, error or report. Unavailable without board copies (local mode).
 */
export function useBoardImport(orgId: string, projectId: string): { available: boolean; busy: boolean; pick: () => void; ui: ReactNode } {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ kind: 'idle' });

  const run = async (file: File) => {
    setState({ kind: 'running', step: 'read', done: 0, total: 1 });
    try {
      const [{ importBoard }, { LocalMediaStore }, { createBoardMediaSession }, { processInWorker }] = await Promise.all([
        import('../export/board-import'),
        import('../media/store'),
        import('../media/session'),
        import('../media/import'),
      ]);
      const local = await LocalMediaStore.open();
      try {
        const report = await importBoard({
          api,
          orgId,
          projectId,
          input: file,
          session: (boardId, store) => createBoardMediaSession({ boardId, api, store, local, process: processInWorker }),
          onProgress: (step, done, total) => setState({ kind: 'running', step, done, total }),
        });
        await invalidate();
        if (report.skipped + report.missing + report.failed === 0) navigate(href('board', { boardId: report.boardId }));
        else setState({ kind: 'report', report });
      } finally {
        local.close();
      }
    } catch (err) {
      setState({ kind: 'error', code: (err as { code?: string }).code ?? 'failed' });
    }
  };

  const ui = (
    <div className="flex flex-col items-end gap-1">
      <FilePicker ref={input} accept=".osboard,.jam,.json,application/json,application/zip" aria-label={t('boardImport.button')} onFiles={([file]) => file && void run(file)} />
      {state.kind === 'running' && (
        <p role="status" className="font-sans text-xs text-fg-muted">
          {t(`boardImport.steps.${state.step}`)}
          {state.step === 'assets' && state.total > 0 ? ` ${state.done} / ${state.total}` : ''}
        </p>
      )}
      {state.kind === 'error' && (
        <p role="alert" className="font-sans text-xs text-danger">
          {t(`boardImport.errors.${['invalid', 'corrupt', 'version', 'too_large'].includes(state.code) ? state.code : 'failed'}`)}
        </p>
      )}
      {state.kind === 'report' && (
        <div role="status" className="flex flex-col items-end gap-1 font-sans text-xs text-fg">
          <p>{t('boardImport.objects', { count: state.report.objects })}</p>
          {state.report.skipped > 0 && <p>{t('boardImport.skipped', { count: state.report.skipped })}</p>}
          {state.report.missing > 0 && <p>{t('boardImport.missing', { count: state.report.missing })}</p>}
          {state.report.failed > 0 && <p>{t('boardImport.failed', { count: state.report.failed })}</p>}
          <Link to={href('board', { boardId: state.report.boardId })} className={linkClass}>
            {t('boardImport.open')}
          </Link>
        </div>
      )}
    </div>
  );
  return { available: api.capabilities.boardCopy, busy: state.kind === 'running', pick: () => input.current?.click(), ui };
}

export function ImportBoardButton({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation();
  const board = useBoardImport(orgId, projectId);
  if (!board.available) return null;
  return (
    <div className="flex flex-col items-end gap-1">
      <Button disabled={board.busy} onClick={board.pick}>
        {t('boardImport.button')}
      </Button>
      {board.ui}
    </div>
  );
}
